import { toast } from 'sonner';
import { PUBLIC_SITE_URL } from '@/lib/urls';

export const REFERRAL_STORAGE_KEY = 'orcamigo_referral_code';

export interface ReferralStats {
  total_invited: number;
  converted: number;
  rewarded: number;
  bonus_earned: number;
}

export function getReferralLink(code: string): string {
  return `${PUBLIC_SITE_URL}/auth?ref=${encodeURIComponent(code.trim())}`;
}

export function normalizeReferralCode(code: string | null | undefined): string | null {
  const normalized = code?.trim().toLowerCase();
  if (!normalized || !/^[a-z0-9_-]{4,64}$/.test(normalized)) return null;
  return normalized;
}

export function storeReferralCode(code: string | null | undefined): string | null {
  const normalized = normalizeReferralCode(code);
  if (!normalized) return null;

  try {
    sessionStorage.setItem(REFERRAL_STORAGE_KEY, normalized);
  } catch {
    // sessionStorage can be unavailable in hardened/private browser contexts.
  }

  return normalized;
}

export function getStoredReferralCode(): string | null {
  try {
    return normalizeReferralCode(sessionStorage.getItem(REFERRAL_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function clearStoredReferralCode(): void {
  try {
    sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // Ignore unavailable storage.
  }
}

export function getReferralCodeFromUrl(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl, PUBLIC_SITE_URL);
    const queryCode = normalizeReferralCode(url.searchParams.get('ref'));
    if (queryCode) return queryCode;

    const hash = url.hash.replace(/^#/, '');
    const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : hash;
    return normalizeReferralCode(new URLSearchParams(hashQuery).get('ref'));
  } catch {
    return null;
  }
}

async function writeToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();

  if (!copied) throw new Error('Clipboard unavailable');
}

export async function copyReferralLink(code: string): Promise<void> {
  await writeToClipboard(getReferralLink(code));
  toast.success('Link de indicação copiado!');
}

export async function shareReferralLink(code: string): Promise<void> {
  const url = getReferralLink(code);

  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Indique e Ganhe — Orçamigo',
        text: 'Crie sua conta no Orçamigo com meu convite e ganhe 3 orçamentos extras!',
        url,
      });
      return;
    } catch (error) {
      if ((error as DOMException)?.name === 'AbortError') return;
    }
  }

  await writeToClipboard(url);
  toast.success('Link de indicação copiado!');
}
